import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon } from "lucide-react";
import { getSetting, saveSetting, listMedia, mediaUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  eyebrow: "", role_line: "", portrait_url: "", bio: "", bio_extended: "",
  journey: { eyebrow: "", title: "", nodes: [] },
  bibliography: { eyebrow: "", title: "", body: "", cta_label: "", cta_to: "" },
  environmental: { eyebrow: "", title: "", orgs: [] },
  gallery: { eyebrow: "", title: "", note: "", images: [] },
  invite: { eyebrow: "", title: "", body: "", primary_cta_label: "", primary_cta_to: "", secondary_cta_label: "", secondary_cta_to: "" },
};

export default function AdminAbout() {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [portraits, setPortraits] = useState([]);
  const [galleryOptions, setGalleryOptions] = useState([]);
  const canEdit = user && (user.role === "owner" || user.role === "editor");

  useEffect(() => {
    let cancelled = false;
    getSetting("about").then(d => { if (!cancelled) setData({ ...EMPTY, ...(d || {}), journey: { ...EMPTY.journey, ...(d?.journey || {}) }, bibliography: { ...EMPTY.bibliography, ...(d?.bibliography || {}) }, environmental: { ...EMPTY.environmental, ...(d?.environmental || {}) }, gallery: { ...EMPTY.gallery, ...(d?.gallery || {}) }, invite: { ...EMPTY.invite, ...(d?.invite || {}) } }); }).catch(() => {});
    listMedia({ kind: "author_photo" }).then(d => { if (!cancelled) setPortraits(d || []); }).catch(() => {});
    Promise.all([
      listMedia({ kind: "research_image" }).catch(() => []),
      listMedia({ kind: "other" }).catch(() => []),
    ]).then(([a, b]) => { if (!cancelled) setGalleryOptions([...(a || []), ...(b || [])]); });
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const setNested = (group, k, v) => setData(d => ({ ...d, [group]: { ...d[group], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await saveSetting("about", data);
      toast.success("About page saved. Reload the public /about page to see updates.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-about-page">
      <PageHeader eyebrow="About the Author" title="About page content" note="Edit the author's About page — portrait, biography, research journey, environmental work, gallery and speaking invitations." />
      {!canEdit && (
        <div className="mt-6 p-4 border-l-2 text-sm" style={{ borderColor: "var(--gi-gold)", background: "var(--gi-ivory-2)", color: "var(--gi-charcoal-2)" }}>
          You are signed in as a Reviewer. Editing is disabled.
        </div>
      )}

      <fieldset disabled={!canEdit} className="mt-8 space-y-8">
        <Card title="Header block">
          <Row>
            <Text label="Eyebrow above name" value={data.eyebrow} onChange={v => set("eyebrow", v)} testId="about-eyebrow-input" />
            <Text label="Role line (small caps under name)" value={data.role_line} onChange={v => set("role_line", v)} testId="about-role-input" />
          </Row>
          <MediaPicker label="Portrait photo" value={data.portrait_url} onChange={v => set("portrait_url", v)} options={portraits} testId="about-portrait-picker" />
          <p className="text-xs" style={{ color: "var(--gi-muted)" }}>Upload photos to Media Library with Kind = "author_photo" — they will appear as options above.</p>
          <TextArea label="Bio (primary paragraph)" rows={4} value={data.bio} onChange={v => set("bio", v)} testId="about-bio-input" />
          <TextArea label="Bio (optional secondary paragraph)" rows={3} value={data.bio_extended} onChange={v => set("bio_extended", v)} testId="about-bio-ext-input" />
        </Card>

        <Card title="Research journey">
          <Row>
            <Text label="Eyebrow" value={data.journey.eyebrow} onChange={v => setNested("journey", "eyebrow", v)} />
            <Text label="Title" value={data.journey.title} onChange={v => setNested("journey", "title", v)} testId="about-journey-title" />
          </Row>
          <StringList label="Journey nodes (one paragraph per row)" value={data.journey.nodes} onChange={v => setNested("journey", "nodes", v)} testPrefix="about-journey-node" />
        </Card>

        <Card title="Bibliography section">
          <Row>
            <Text label="Eyebrow" value={data.bibliography.eyebrow} onChange={v => setNested("bibliography", "eyebrow", v)} />
            <Text label="Title" value={data.bibliography.title} onChange={v => setNested("bibliography", "title", v)} />
          </Row>
          <TextArea label="Body" rows={2} value={data.bibliography.body} onChange={v => setNested("bibliography", "body", v)} />
          <Row>
            <Text label="CTA label" value={data.bibliography.cta_label} onChange={v => setNested("bibliography", "cta_label", v)} />
            <Text label="CTA link" value={data.bibliography.cta_to} onChange={v => setNested("bibliography", "cta_to", v)} />
          </Row>
        </Card>

        <Card title="Environmental work">
          <Row>
            <Text label="Eyebrow" value={data.environmental.eyebrow} onChange={v => setNested("environmental", "eyebrow", v)} />
            <Text label="Title" value={data.environmental.title} onChange={v => setNested("environmental", "title", v)} />
          </Row>
          <OrgList value={data.environmental.orgs} onChange={v => setNested("environmental", "orgs", v)} />
        </Card>

        <Card title="Author gallery" note="Upload images to Media Library with Kind = 'research_image' — pick as many as you want.">
          <Row>
            <Text label="Eyebrow" value={data.gallery.eyebrow} onChange={v => setNested("gallery", "eyebrow", v)} />
            <Text label="Title" value={data.gallery.title} onChange={v => setNested("gallery", "title", v)} />
          </Row>
          <Text label="Note (small text under gallery)" value={data.gallery.note} onChange={v => setNested("gallery", "note", v)} />
          <GalleryPicker value={data.gallery.images} onChange={v => setNested("gallery", "images", v)} options={galleryOptions} />
        </Card>

        <Card title="Invitation / CTA block">
          <Row>
            <Text label="Eyebrow" value={data.invite.eyebrow} onChange={v => setNested("invite", "eyebrow", v)} />
            <Text label="Title" value={data.invite.title} onChange={v => setNested("invite", "title", v)} />
          </Row>
          <TextArea label="Body" rows={2} value={data.invite.body} onChange={v => setNested("invite", "body", v)} />
          <Row>
            <Text label="Primary CTA label" value={data.invite.primary_cta_label} onChange={v => setNested("invite", "primary_cta_label", v)} />
            <Text label="Primary CTA link" value={data.invite.primary_cta_to} onChange={v => setNested("invite", "primary_cta_to", v)} />
          </Row>
          <Row>
            <Text label="Secondary CTA label" value={data.invite.secondary_cta_label} onChange={v => setNested("invite", "secondary_cta_label", v)} />
            <Text label="Secondary CTA link" value={data.invite.secondary_cta_to} onChange={v => setNested("invite", "secondary_cta_to", v)} />
          </Row>
        </Card>
      </fieldset>

      {canEdit && (
        <button onClick={save} disabled={saving} className="btn-maroon mt-8" data-testid="about-save-btn">
          <Save size={12} /> {saving ? "Saving…" : "Save About page"}
        </button>
      )}
    </div>
  );
}

const Card = ({ title, note, children }) => (
  <section className="border bg-white" style={{ borderColor: "var(--gi-border)" }}>
    <div className="px-6 py-4 border-b" style={{ borderColor: "var(--gi-border)" }}>
      <div className="font-heading text-xl" style={{ color: "var(--gi-maroon)" }}>{title}</div>
      {note && <p className="mt-1 text-xs" style={{ color: "var(--gi-muted)" }}>{note}</p>}
    </div>
    <div className="p-6 space-y-4">{children}</div>
  </section>
);
const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
const Text = ({ label, value, onChange, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <input className="gi-input" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);
const TextArea = ({ label, value, onChange, rows = 3, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <textarea rows={rows} className="gi-input" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);

const MediaPicker = ({ label, value, onChange, options, testId }) => (
  <div data-testid={testId}>
    <label className="gi-label">{label}</label>
    <div className="grid gap-3 grid-cols-4">
      <label className="border p-2 cursor-pointer flex flex-col items-center gap-1 text-xs" style={{ borderColor: !value ? "var(--gi-maroon)" : "var(--gi-border)" }}>
        <input type="radio" checked={!value} onChange={() => onChange("")} className="sr-only" />
        <div className="w-full aspect-square bg-[var(--gi-ivory-2)] flex items-center justify-center"><ImageIcon size={20} style={{ color: "var(--gi-muted)" }} /></div>
        <span>Default</span>
      </label>
      {(options || []).slice(0, 7).map(o => (
        <label key={o.id} className="border p-2 cursor-pointer flex flex-col items-center gap-1 text-xs" style={{ borderColor: value === o.url ? "var(--gi-maroon)" : "var(--gi-border)" }}>
          <input type="radio" checked={value === o.url} onChange={() => onChange(o.url)} className="sr-only" />
          <img src={mediaUrl(o.url)} alt={o.alt_text || o.filename} className="w-full aspect-square object-cover" />
          <span className="truncate w-full">{o.filename}</span>
        </label>
      ))}
    </div>
  </div>
);

const StringList = ({ label, value, onChange, testPrefix }) => {
  const list = value || [];
  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      <label className="gi-label">{label}</label>
      <div className="space-y-2">
        {list.map((row, i) => (
          <div key={`row-${i}`} className="grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
            <textarea rows={2} className="gi-input" value={row} onChange={e => onChange(list.map((v, j) => j === i ? e.target.value : v))} data-testid={`${testPrefix}-${i}`} />
            <button type="button" className="btn-ghost" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={12} /></button>
            <button type="button" className="btn-ghost" onClick={() => move(i, 1)} disabled={i === list.length - 1}><ArrowDown size={12} /></button>
            <button type="button" className="btn-ghost" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" className="btn-outline" onClick={() => onChange([...list, ""])}><Plus size={12} /> Add row</button>
      </div>
    </div>
  );
};

const OrgList = ({ value, onChange }) => {
  const list = value || [];
  const setAt = (i, patch) => onChange(list.map((c, j) => j === i ? { ...c, ...patch } : c));
  return (
    <div>
      <label className="gi-label">Organisations</label>
      <div className="space-y-3">
        {list.map((row, i) => (
          <div key={`org-${i}`} className="border p-3 grid gap-2" style={{ borderColor: "var(--gi-border)" }}>
            <input className="gi-input" placeholder="Title" value={row.title || ""} onChange={e => setAt(i, { title: e.target.value })} data-testid={`org-title-${i}`} />
            <textarea rows={2} className="gi-input" placeholder="Description" value={row.body || ""} onChange={e => setAt(i, { body: e.target.value })} data-testid={`org-body-${i}`} />
            <input className="gi-input" placeholder="Website URL (optional)" value={row.url || ""} onChange={e => setAt(i, { url: e.target.value })} data-testid={`org-url-${i}`} />
            <button type="button" className="btn-ghost" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 size={12} /> Remove</button>
          </div>
        ))}
        <button type="button" className="btn-outline" onClick={() => onChange([...list, { title: "", body: "", url: "" }])}><Plus size={12} /> Add organisation</button>
      </div>
    </div>
  );
};

const GalleryPicker = ({ value, onChange, options }) => {
  const list = value || [];
  const toggle = (url) => {
    if (list.includes(url)) {
      onChange(list.filter(u => u !== url));
    } else {
      onChange([...list, url]);
    }
  };
  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      <label className="gi-label">Selected gallery images (click to add/remove; drag order via arrows below)</label>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        {(options || []).map(o => {
          const selected = list.includes(o.url);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.url)}
              className="border p-2 flex flex-col items-center gap-1 text-xs cursor-pointer"
              style={{ borderColor: selected ? "var(--gi-maroon)" : "var(--gi-border)", background: selected ? "rgba(106,30,37,0.06)" : "transparent" }}
              data-testid={`gallery-toggle-${o.id}`}
            >
              <img src={mediaUrl(o.url)} alt={o.alt_text || o.filename} className="w-full aspect-square object-cover" />
              <span className="truncate w-full">{selected ? "✓ Selected" : o.filename}</span>
            </button>
          );
        })}
      </div>
      {list.length > 0 && (
        <div className="mt-4">
          <div className="gi-label">Order</div>
          <ol className="space-y-2">
            {list.map((url, i) => (
              <li key={`selected-${i}-${url}`} className="grid gap-2 sm:grid-cols-[auto_1fr_auto_auto_auto] items-center">
                <img src={mediaUrl(url)} alt="" className="w-12 h-12 object-cover border" style={{ borderColor: "var(--gi-border)" }} />
                <span className="text-xs truncate" style={{ color: "var(--gi-muted)" }}>{url}</span>
                <button type="button" className="btn-ghost" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={12} /></button>
                <button type="button" className="btn-ghost" onClick={() => move(i, 1)} disabled={i === list.length - 1}><ArrowDown size={12} /></button>
                <button type="button" className="btn-ghost" onClick={() => toggle(url)}><Trash2 size={12} /></button>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};
